import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Checkbox,
  Typography,
  MenuItem,
  Select,
  FormControl,
  Container,
  Chip,
  Paper,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import PropTypes from "prop-types";

const ContactForm = () => {
  const [form, setForm] = useState(() => {
    const savedForm = localStorage.getItem("contactForm");
    return savedForm
      ? JSON.parse(savedForm)
      : {
          name: "",
          company: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
          agree: false,
        };
  });

  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    localStorage.setItem("contactForm", JSON.stringify(form));
  }, [form]);

  const validate = useCallback(() => {
    const newErrors = {};
    if (!form.name) newErrors.name = "必須項目です";
    if (!form.company) newErrors.company = "必須項目です";
    if (!form.email) newErrors.email = "必須項目です";
    if (!form.phone) newErrors.phone = "必須項目です";
    if (!form.subject) newErrors.subject = "必須項目です";
    if (!form.message) newErrors.message = "必須項目です";
    if (!form.agree) newErrors.agree = "同意が必要です";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form]);

  useEffect(() => {
    setIsValid(validate());
  }, [form, validate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    console.log("onSubmit called");

    if (validate()) {
      try {
        console.log("Sending form data:", form);
        const response = await axios.post("/send-mail.php", form, {
          responseType: "text",
        });

        if (response.data === "送信成功") {
          alert("送信に成功しました！");
          setForm({
            name: "",
            company: "",
            email: "",
            phone: "",
            subject: "",
            message: "",
            agree: false,
          });
          setSubmitted(false);
          localStorage.removeItem("contactForm");
        } else {
          alert("送信に失敗しました");
        }
      } catch (error) {
        alert("通信エラーが発生しました: " + error.message);
      }

      console.log(form);
    }
  };

  const RequiredLabel = ({ label }) => (
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      <Chip
        label="必須"
        color="error"
        size="small"
        sx={{ mr: 1, borderRadius: 2, fontWeight: "bold" }}
      />
      <Typography sx={{ fontWeight: "bold", fontFamily: "Roboto, sans-serif" }}>
        {label}
      </Typography>
    </Box>
  );

  RequiredLabel.propTypes = {
    label: PropTypes.string.isRequired,
  };

  return (
    <Container id="contact" maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
      <Box
        sx={{
          display: { xs: "block", md: "flex" },
          alignItems: { md: "flex-start" },
          gap: { md: 4 },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: "30%" },
            textAlign: { xs: "left", md: "left" },
            ml: "10px",
            mb: { xs: 5, md: 0 },
            pl: { md: 8 },
            pt: { md: 5 },
          }}
        >
          <Typography
            sx={{
              color: "#E40614",
              fontWeight: "bold",
              fontSize: { xs: "40px", md: "64px" },
              lineHeight: 1,
              fontFamily: "Roboto, sans-serif",
            }}
          >
            Contact
          </Typography>
          <Typography
            sx={{
              color: "#E40614",
              mt: 2,
              fontSize: { xs: "16px", md: "20px" },
              fontFamily: "Roboto, sans-serif",
            }}
          >
            お問い合わせ
          </Typography>
        </Box>
        <Box sx={{ width: { xs: "100%", md: "70%" } }}>
          <Paper sx={{ p: { xs: 3, md: 5 }, boxShadow: "none" }}>
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Box mb={4}>
                <RequiredLabel label="お名前" />
                <TextField
                  fullWidth
                  placeholder="例）山田 太郎"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  error={submitted && !!errors.name}
                  helperText={submitted ? errors.name : ""}
                  sx={{
                    backgroundColor: "#f9f9f9",
                    borderRadius: 2,
                    "& fieldset": { border: "none" },
                  }}
                  InputProps={{ sx: { height: 56 } }}
                />
              </Box>
              <Box mb={4}>
                <RequiredLabel label="会社名" />
                <TextField
                  fullWidth
                  placeholder="例）YouProduce株式会社"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  error={submitted && !!errors.company}
                  helperText={submitted ? errors.company : ""}
                  sx={{
                    backgroundColor: "#f9f9f9",
                    borderRadius: 2,
                    "& fieldset": { border: "none" },
                  }}
                  InputProps={{ sx: { height: 56 } }}
                />
              </Box>
              <Box mb={4}>
                <RequiredLabel label="メールアドレス" />
                <TextField
                  fullWidth
                  type="email"
                  placeholder="例）example@upro.io"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  error={submitted && !!errors.email}
                  helperText={submitted ? errors.email : ""}
                  sx={{
                    backgroundColor: "#f9f9f9",
                    borderRadius: 2,
                    "& fieldset": { border: "none" },
                  }}
                  InputProps={{ sx: { height: 56 } }}
                />
              </Box>
              <Box mb={4}>
                <RequiredLabel label="電話番号" />
                <TextField
                  fullWidth
                  type="tel"
                  placeholder="例）06-1234-5678"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  error={submitted && !!errors.phone}
                  helperText={submitted ? errors.phone : ""}
                  sx={{
                    backgroundColor: "#f9f9f9",
                    borderRadius: 2,
                    "& fieldset": { border: "none" },
                  }}
                  InputProps={{ sx: { height: 56 } }}
                />
              </Box>
              <Box mb={4}>
                <RequiredLabel label="お問い合わせ項目" />
                <FormControl
                  fullWidth
                  sx={{
                    backgroundColor: "#f9f9f9",
                    borderRadius: 2,
                    "& fieldset": { border: "none" },
                  }}
                  error={submitted && !!errors.subject}
                >
                  <Select
                    name="subject"
                    value={form.subject}
                    displayEmpty
                    onChange={handleChange}
                    sx={{ height: 56 }}
                  >
                    <MenuItem value="">
                      <em>お問い合わせ項目を選択</em>
                    </MenuItem>
                    <MenuItem value="非公開実績が載った資料がほしい">
                      非公開実績が載った資料がほしい
                    </MenuItem>
                    <MenuItem value="まずは相談したい">
                      まずは相談したい
                    </MenuItem>
                    <MenuItem value="見積もりがほしい">
                      見積もりがほしい
                    </MenuItem>
                    <MenuItem value="協業を相談したい">
                      協業を相談したい
                    </MenuItem>
                    <MenuItem value="営業目的">営業目的</MenuItem>
                    <MenuItem value="取材依頼">取材依頼</MenuItem>
                  </Select>
                  {submitted && errors.subject && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ pl: 2, mt: 0.5, fontFamily: "Roboto, sans-serif" }}
                    >
                      {errors.subject}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <Box mb={4}>
                <RequiredLabel label="お問い合わせ内容" />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="お問い合わせ内容を入力"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  error={submitted && !!errors.message}
                  helperText={submitted ? errors.message : ""}
                  sx={{
                    backgroundColor: "#f9f9f9",
                    borderRadius: 2,
                    "& fieldset": { border: "none" },
                  }}
                />
              </Box>
              <Box mb={3} sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Checkbox
                    name="agree"
                    checked={form.agree}
                    onChange={handleChange}
                    sx={{ mr: 0.5, p: 0.5 }}
                  />
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    <MuiLink
                      component={RouterLink}
                      to="/privacy-policy"
                      sx={{
                        color: "#1A1A50",
                        textDecoration: "underline",
                        verticalAlign: "baseline",
                        mx: 0.5,
                      }}
                    >
                      個人情報保護方針
                    </MuiLink>
                    に同意します
                  </Typography>
                </Box>
                {submitted && errors.agree && (
                  <Typography
                    color="error"
                    variant="caption"
                    component="div"
                    sx={{ mt: 0.5, fontFamily: "Roboto, sans-serif" }}
                  >
                    {errors.agree}
                  </Typography>
                )}
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={!isValid}
                sx={{
                  backgroundColor: "#1A1A50",
                  color: "#fff",
                  borderRadius: 8,
                  py: 1.8,
                  fontWeight: "bold",
                  transition:
                    "transform 0.2s ease-in-out, background-color 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#14143d",
                    transform: "scale(1.1, 1.1)",
                  },
                  "&:disabled": {
                    backgroundColor: "grey.400",
                    transform: "scale(1)",
                  },
                }}
              >
                送信
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default ContactForm;
